/**
 * StorePricingContext - 매장별 가격 정책 상태 관리
 *
 * #907: 매장별 세율(tax_rate)과 통화(currency)를 동적으로 관리합니다.
 * API에서 내려오는 매장별 세율을 결제/영수증/세금계산서에 전파합니다.
 *
 * @example
 * // AppProvider 내부에서 사용
 * <StorePricingProvider>
 *   <App />
 * </StorePricingProvider>
 *
 * // 매장 진입 시 설정
 * const { setStorePricing } = useStorePricing();
 * setStorePricing(store.taxRate, store.currency);
 *
 * // 결제 계산 시 사용
 * const { taxRate, currency } = useStorePricing();
 */
import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';
import { PRICING } from '@/constants/pricing';
import type { Currency } from '@/types';
import type { StorePointsConfig } from '@/lib/api/types';
import { normalizePointsConfig } from '@/lib/points/normalizePointsConfig';

interface StorePricingContextValue {
  /** 세율 (비율: 0.16) */
  taxRate: number;
  /** 세율 퍼센트 (표시용: 16) */
  taxRatePercent: number;
  /** 매장 통화 */
  currency: Currency;
  /** #285 매장 포인트 정책 (정규화됨. 비활성·계약 위반 시 null) */
  pointsConfig: StorePointsConfig | null;
  /** #285 포인트 UI 게이트 플래그 (pointsConfig !== null) */
  pointsEnabled: boolean;
  /** 매장 가격/포인트 정보 설정 */
  setStorePricing: (
    taxRate?: number | null,
    currency?: string | null,
    pointsConfig?: StorePointsConfig | null
  ) => void;
  /** 초기화 */
  clearStorePricing: () => void;
}

const StorePricingContext = createContext<StorePricingContextValue | null>(null);

interface StorePricingProviderProps {
  children: ReactNode;
  /** 기본 통화 (AppContext의 currency와 연동) */
  defaultCurrency?: Currency;
}

/**
 * StorePricingProvider
 *
 * 매장별 세율과 통화를 관리하는 Context Provider입니다.
 * 세율은 API에서 퍼센트로 내려오지만, 내부적으로는 비율로 관리합니다.
 *
 * @param children - 자식 컴포넌트
 * @param defaultCurrency - 기본 통화 (기본값: 'MXN')
 */
export function StorePricingProvider({
  children,
  defaultCurrency = 'MXN',
}: StorePricingProviderProps) {
  const [storeTaxRate, setStoreTaxRate] = useState<number | null>(null);
  const [storeCurrency, setStoreCurrency] = useState<string | null>(null);
  const [pointsConfig, setPointsConfig] = useState<StorePointsConfig | null>(null);

  // 매장 세율이 없으면 기본값 사용
  const taxRate = storeTaxRate ?? PRICING.DEFAULT_TAX_RATE;
  const taxRatePercent = Math.round(taxRate * 100);
  const currency = (storeCurrency ?? defaultCurrency) as Currency;
  const pointsEnabled = pointsConfig !== null;

  /**
   * 매장 가격/포인트 정보 설정
   * @param taxRate - 세율 (비율 형태: 0.16)
   * @param currency - 통화 코드 ('MXN', 'USD' 등)
   * @param pointsRaw - 매장 포인트 정책 (정규화 전). null/undefined 면 포인트 UI 비활성
   */
  const setStorePricing = useCallback(
    (
      tr?: number | null,
      curr?: string | null,
      pointsRaw?: StorePointsConfig | null
    ) => {
      setStoreTaxRate(tr ?? null);
      setStoreCurrency(curr ?? null);
      setPointsConfig(normalizePointsConfig(pointsRaw));
    },
    []
  );

  /**
   * 매장 가격 정보 초기화 (매장 이탈 시 호출)
   */
  const clearStorePricing = useCallback(() => {
    setStoreTaxRate(null);
    setStoreCurrency(null);
    setPointsConfig(null);
  }, []);

  const value = useMemo(
    () => ({
      taxRate,
      taxRatePercent,
      currency,
      pointsConfig,
      pointsEnabled,
      setStorePricing,
      clearStorePricing,
    }),
    [taxRate, taxRatePercent, currency, pointsConfig, pointsEnabled, setStorePricing, clearStorePricing]
  );

  return (
    <StorePricingContext.Provider value={value}>
      {children}
    </StorePricingContext.Provider>
  );
}

/**
 * useStorePricing - 매장별 가격 정책 훅
 *
 * @throws StorePricingProvider 외부에서 사용 시 에러
 */
export function useStorePricing(): StorePricingContextValue {
  const ctx = useContext(StorePricingContext);
  if (!ctx) {
    throw new Error('useStorePricing must be used within StorePricingProvider');
  }
  return ctx;
}

/**
 * useTaxRate - 세율만 사용하는 간편 훅
 *
 * @returns taxRate (비율), taxRatePercent (퍼센트)
 */
export function useTaxRate() {
  const { taxRate, taxRatePercent } = useStorePricing();
  return { taxRate, taxRatePercent };
}

/**
 * useStoreCurrency - 매장 통화만 사용하는 간편 훅
 *
 * @returns currency (매장 통화)
 */
export function useStoreCurrency() {
  const { currency } = useStorePricing();
  return { currency };
}
